import { CheckCircle2, KeyRound, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useDeleteOAuthApp, useOAuthApps, useSaveOAuthApp } from "@/api/hooks";
import type { OAuthAppSummary } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Human-readable vendor labels + extra-field hints. */
const VENDOR_META: Record<string, { label: string; docsUrl: string; extraHint?: string }> = {
	microsoft_365: {
		label: "Microsoft 365",
		docsUrl: "https://portal.azure.com",
		extraHint: 'Optional: {"authority_tenant": "organizations"} (default)',
	},
	google_workspace: {
		label: "Google Workspace",
		docsUrl: "https://console.cloud.google.com",
	},
	hubspot: { label: "HubSpot", docsUrl: "https://developers.hubspot.com" },
	slack: { label: "Slack", docsUrl: "https://api.slack.com/apps" },
	docusign: {
		label: "DocuSign",
		docsUrl: "https://admindemo.docusign.com",
		extraHint: 'Required: {"environment": "production"|"demo"}',
	},
};

interface VendorFormState {
	clientId: string;
	clientSecret: string;
	extraJson: string; // stored as JSON text; parsed on submit
	error: string | null;
	success: boolean;
}

function blankForm(): VendorFormState {
	return { clientId: "", clientSecret: "", extraJson: "", error: null, success: false };
}

function VendorForm({
	vendor,
	existing,
}: {
	vendor: string;
	existing: OAuthAppSummary | undefined;
}) {
	const save = useSaveOAuthApp();
	const del = useDeleteOAuthApp();
	const meta = VENDOR_META[vendor] ?? { label: vendor, docsUrl: "" };

	const [state, setState] = useState<VendorFormState>(() => ({
		...blankForm(),
		clientId: existing?.client_id ?? "",
		extraJson: existing ? JSON.stringify(existing.extra, null, 2) : "",
	}));

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		let extra: Record<string, unknown> = {};
		if (state.extraJson.trim()) {
			try {
				extra = JSON.parse(state.extraJson) as Record<string, unknown>;
			} catch (err) {
				setState((s) => ({
					...s,
					error: `Invalid JSON in extra: ${(err as Error).message}`,
					success: false,
				}));
				return;
			}
		}
		try {
			await save.mutateAsync({
				vendor,
				body: { client_id: state.clientId, client_secret: state.clientSecret, extra },
			});
			setState((s) => ({ ...s, clientSecret: "", error: null, success: true }));
		} catch (err) {
			setState((s) => ({
				...s,
				error: err instanceof ApiError ? err.detail : "Unable to save.",
				success: false,
			}));
		}
	}

	async function onDelete() {
		if (
			!window.confirm(
				`Delete Veridon-level OAuth creds for ${meta.label}? Onboarding this vendor will fail until re-pasted.`,
			)
		) {
			return;
		}
		try {
			await del.mutateAsync(vendor);
			setState(blankForm());
		} catch {
			// surfaced via del.error if rendered; not critical here
		}
	}

	return (
		<Card>
			<CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
				<div className="space-y-1">
					<CardTitle className="flex items-center gap-2 text-base">
						{meta.label}
						{existing ? (
							<Badge variant="success">
								<CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
								Configured
							</Badge>
						) : (
							<Badge variant="outline" className="text-muted-foreground">
								Not configured
							</Badge>
						)}
					</CardTitle>
					<CardDescription>
						<span className="font-mono text-xs">{vendor}</span>
						{meta.docsUrl ? (
							<>
								{" · "}
								<a
									href={meta.docsUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="underline decoration-dotted underline-offset-4"
								>
									Vendor console
								</a>
							</>
						) : null}
					</CardDescription>
				</div>
				{existing ? (
					<Button
						size="sm"
						variant="outline"
						onClick={onDelete}
						disabled={del.isPending}
						title="Delete stored creds"
					>
						<Trash2 className="mr-1 h-3 w-3" aria-hidden />
						Delete
					</Button>
				) : null}
			</CardHeader>
			<CardContent>
				<form className="space-y-3" onSubmit={onSubmit} noValidate>
					<div className="space-y-1">
						<Label htmlFor={`${vendor}-client-id`}>Client ID</Label>
						<Input
							id={`${vendor}-client-id`}
							required
							value={state.clientId}
							onChange={(e) =>
								setState((s) => ({ ...s, clientId: e.target.value, success: false }))
							}
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor={`${vendor}-client-secret`}>Client secret</Label>
						<Input
							id={`${vendor}-client-secret`}
							type="password"
							autoComplete="off"
							required
							placeholder={
								existing ? "Enter new secret to rotate (current hidden)" : "Paste client secret"
							}
							value={state.clientSecret}
							onChange={(e) =>
								setState((s) => ({ ...s, clientSecret: e.target.value, success: false }))
							}
						/>
						<p className="text-xs text-muted-foreground">
							Encrypted at rest via Fernet. Never echoed back by the API.
						</p>
					</div>
					{meta.extraHint ? (
						<div className="space-y-1">
							<Label htmlFor={`${vendor}-extra`}>Extra (JSON, optional)</Label>
							<Input
								id={`${vendor}-extra`}
								placeholder={meta.extraHint}
								value={state.extraJson}
								onChange={(e) =>
									setState((s) => ({ ...s, extraJson: e.target.value, success: false }))
								}
							/>
						</div>
					) : null}
					{state.error ? (
						<p className="text-sm text-destructive" role="alert">
							{state.error}
						</p>
					) : null}
					{state.success ? (
						<output className="block text-sm text-emerald-600">
							{existing ? "Rotated." : "Saved."}
						</output>
					) : null}
					<Button
						type="submit"
						size="sm"
						disabled={save.isPending || !state.clientId || !state.clientSecret}
					>
						<KeyRound className="mr-1 h-3 w-3" aria-hidden />
						{save.isPending ? "Saving…" : existing ? "Rotate" : "Save"}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}

/** Top-level section: lists all supported vendors with configure/rotate forms. */
export function OAuthAppsSection() {
	const { data, isLoading, error } = useOAuthApps();

	if (isLoading) {
		return <p className="text-sm text-muted-foreground">Loading OAuth apps…</p>;
	}
	if (error) {
		return (
			<p className="text-sm text-destructive">
				{error instanceof ApiError ? error.detail : "Failed to load OAuth apps."}
			</p>
		);
	}
	if (!data) return null;

	const configuredByVendor = new Map(data.apps.map((a) => [a.vendor, a]));

	return (
		<section aria-labelledby="oauth-apps-heading" className="space-y-4">
			<div>
				<h3 id="oauth-apps-heading" className="text-lg font-semibold tracking-tight">
					Veridon-level OAuth apps
				</h3>
				<p className="text-sm text-muted-foreground">
					Registered once per vendor in their developer console — the onboarding wizard pushes these
					to each new tenant portal's Fly secrets. See{" "}
					<a
						href="https://github.com/veridonconsulting/veridon-kit/blob/main/docs/oauth-app-registration.md"
						target="_blank"
						rel="noopener noreferrer"
						className="underline decoration-dotted underline-offset-4"
					>
						the playbook
					</a>{" "}
					for the per-vendor setup.
				</p>
			</div>
			<div className="grid gap-3">
				{data.supported_vendors.map((vendor) => (
					<VendorForm key={vendor} vendor={vendor} existing={configuredByVendor.get(vendor)} />
				))}
			</div>
		</section>
	);
}
